import {
	ConvertedImageStatusRef,
	isConvertedImageLookupFailure,
	isTerminalConvertedImageStatus,
	lookupConvertedImageStatusesBatched,
} from '../thunks/converted_images_thunks';

export interface PendingOriginal {
	fileId?: string;
	s3ObjectKey?: string;
}

/**
 * One conversion poll for the whole page, not one per image.
 *
 * Every tile asking on its own was the wrong shape twice over. A notes feed renders a hook per
 * photo — plus the off-screen lightbox copies — so a single WhatsApp-style collage produced five
 * or six independent backoff loops, and once their schedules drifted apart the request coalescer
 * had nothing left to merge. Worse, each loop kept its answers in component state, so anything
 * that remounted a tile (a websocket typing tick re-rendering the feed, StrictMode's double mount
 * in development) threw away a resolved conversion and started again from `hasLoaded: false` —
 * which is what pinned a tile on "Converting…" while the network tab showed the response saying
 * `ready`.
 *
 * So the state lives here instead, outside React: results are cached for the session and read
 * synchronously on mount, and subscribers are refcounted so exactly one loop runs for the union of
 * whatever is currently on screen.
 */

// Gaps between polls, then a steady tick, then give up. The cadence is shared across subscribers
// so it does not multiply with the number of images on screen; the give-up budget below is charged
// per original, so a photo added late gets a full window of its own.
const POLL_DELAYS_MS = [2000, 3000, 5000, 8000, 13000];
const STEADY_POLL_DELAY_MS = 15000;
const MAX_POLL_DURATION_MS = 120000;

const getPollDelayMs = (attempt: number): number =>
	attempt < POLL_DELAYS_MS.length ? POLL_DELAYS_MS[attempt] : STEADY_POLL_DELAY_MS;

export const getOriginalKey = (original: PendingOriginal): string =>
	`${original.fileId || ''}|${original.s3ObjectKey || ''}`;

// Cached for the session: a conversion that resolved once must not be re-fetched, and must be
// readable synchronously by a tile that mounts later.
const refsByFileId = new Map<string, ConvertedImageStatusRef>();
const refsByS3ObjectKey = new Map<string, ConvertedImageStatusRef>();
// Originals we have a final answer for — ready, failed, or absent from a response.
const resolvedKeys = new Set<string>();
// Originals a subscriber has ever registered. Distinguishes "not asked yet" from "asked and gave
// up": both leave the key out of `resolvedKeys`, but only the first still owes us an answer.
const askedKeys = new Set<string>();
// Polling time charged to each original, and the ones whose own window is spent. Per-original
// rather than one shared deadline: a photo added late must get a full budget without renewing the
// deadline of one that has been waiting since the start.
const elapsedMsByKey = new Map<string, number>();
const gaveUpKeys = new Set<string>();

const subscriberCounts = new Map<string, number>();
const originalsByKey = new Map<string, PendingOriginal>();
const listeners = new Set<() => void>();

let timeoutId: ReturnType<typeof setTimeout> | null = null;
let attempt = 0;
let inFlight = false;
// Bumped on every change so a subscriber can tell "nothing known yet" from "answered".
let version = 0;

export const getStoreVersion = (): number => version;

export const findStoredRef = (original: PendingOriginal | null): ConvertedImageStatusRef | null => {
	if (!original) {
		return null;
	}
	if (original.fileId && refsByFileId.has(original.fileId)) {
		return refsByFileId.get(original.fileId) as ConvertedImageStatusRef;
	}
	if (original.s3ObjectKey && refsByS3ObjectKey.has(original.s3ObjectKey)) {
		return refsByS3ObjectKey.get(original.s3ObjectKey) as ConvertedImageStatusRef;
	}
	return null;
};

/** True while a final answer is still expected. False once resolved, or once the budget is spent. */
export const isStillConverting = (original: PendingOriginal | null): boolean => {
	if (!original) {
		return false;
	}
	const key = getOriginalKey(original);
	// Subscription happens in an effect, so the first render of a HEIC always lands here before the
	// poll exists. Reporting "not converting" then let the caller kick off the main-thread heic2any
	// decode that the backend lookup was supposed to pre-empt.
	if (!askedKeys.has(key)) {
		return true;
	}
	if (resolvedKeys.has(key)) {
		const ref = findStoredRef(original);
		return !!ref && !isTerminalConvertedImageStatus(ref.status);
	}
	// Nothing further is coming for this one — its own budget ran out, or the loop was abandoned —
	// so stop showing a spinner even though the loop may still be running for a later photo.
	if (gaveUpKeys.has(key)) {
		return false;
	}
	// Asked, unanswered, not abandoned: an answer is still expected. Deliberately not gated on a
	// live subscriber, because a remount has none on its first render — reporting "not converting"
	// there started the local decode the backend lookup exists to pre-empt.
	return true;
};

const notify = () => {
	version += 1;
	listeners.forEach((listener) => listener());
};

const getUnresolvedOriginals = (): PendingOriginal[] =>
	Array.from(subscriberCounts.keys())
		.filter((key) => !resolvedKeys.has(key) && !gaveUpKeys.has(key))
		.map((key) => originalsByKey.get(key))
		.filter((original): original is PendingOriginal => !!original);

const stop = () => {
	if (timeoutId !== null) {
		clearTimeout(timeoutId);
		timeoutId = null;
	}
};

// Give up on whatever is still outstanding. `gaveUpKeys` is the one signal `isStillConverting`
// reads for "no answer is coming", so a loop that walks away has to record it.
const abandonUnresolved = () => {
	getUnresolvedOriginals().forEach((original) => gaveUpKeys.add(getOriginalKey(original)));
};

/**
 * Charge the next gap to every original still waiting, retire the ones whose own window it would
 * overrun, and schedule the next tick if anything is left.
 */
const chargeBudgetAndScheduleNext = () => {
	const nextDelayMs = getPollDelayMs(attempt);
	const stillWaiting = getUnresolvedOriginals().filter((original) => {
		const key = getOriginalKey(original);
		const spentMs = (elapsedMsByKey.get(key) || 0) + nextDelayMs;
		if (spentMs > MAX_POLL_DURATION_MS) {
			gaveUpKeys.add(key);
			return false;
		}
		elapsedMsByKey.set(key, spentMs);
		return true;
	});
	if (stillWaiting.length === 0) {
		stop();
		return;
	}
	attempt += 1;
	timeoutId = setTimeout(poll, nextDelayMs);
};

const poll = () => {
	const unresolved = getUnresolvedOriginals();
	if (unresolved.length === 0) {
		stop();
		return;
	}

	const fileIds = unresolved
		.map((original) => original.fileId)
		.filter((fileId): fileId is string => !!fileId);
	const s3ObjectKeys = unresolved
		.map((original) => original.s3ObjectKey)
		.filter((s3ObjectKey): s3ObjectKey is string => !!s3ObjectKey);

	inFlight = true;
	lookupConvertedImageStatusesBatched({ fileIds, s3ObjectKeys })
		.then((refs) => {
			inFlight = false;
			refs.forEach((ref) => {
				if (ref.fileId) {
					refsByFileId.set(ref.fileId, ref);
				}
				if (ref.s3ObjectKey) {
					refsByS3ObjectKey.set(ref.s3ObjectKey, ref);
				}
			});
			// Absent from the response means no conversion is expected, which is as final an answer
			// as `ready` — both stop the loop for that original.
			unresolved.forEach((original) => {
				const ref = findStoredRef(original);
				if (!ref || isTerminalConvertedImageStatus(ref.status)) {
					resolvedKeys.add(getOriginalKey(original));
				}
			});

			chargeBudgetAndScheduleNext();
			notify();
		})
		.catch((reason) => {
			inFlight = false;
			// A failed lookup says nothing about the image, so retry inside the same budget rather
			// than leaving these originals unresolved — they are deliberately NOT added to
			// `resolvedKeys` here, which is what let one dropped request hide a landed derivative
			// for the rest of the tab's life.
			if (isConvertedImageLookupFailure(reason)) {
				chargeBudgetAndScheduleNext();
				notify();
				return;
			}
			// An auth failure, or an error we cannot classify: give up on what is outstanding so
			// callers fall back to the original instead of sitting on a placeholder forever.
			abandonUnresolved();
			stop();
			notify();
		});
};

const startIfIdle = () => {
	if (timeoutId === null && !inFlight && getUnresolvedOriginals().length > 0) {
		attempt = 0;
		poll();
	}
};

/**
 * Register interest in a set of originals and get notified as answers arrive. Returns the
 * unsubscribe. Refcounted: the loop runs while at least one subscriber wants an unresolved
 * original, and a remount that re-subscribes to something already answered costs no request.
 */
export const subscribeToConvertedImageStatus = (
	originals: PendingOriginal[],
	onChange: () => void
): (() => void) => {
	listeners.add(onChange);
	const hasFreshOriginal = originals.some((original) => {
		const key = getOriginalKey(original);
		return !askedKeys.has(key) && !resolvedKeys.has(key);
	});
	originals.forEach((original) => {
		const key = getOriginalKey(original);
		originalsByKey.set(key, original);
		askedKeys.add(key);
		subscriberCounts.set(key, (subscriberCounts.get(key) || 0) + 1);
	});

	if (originals.length > 0) {
		// Restart the backoff ramp so a photo added mid-flight is answered quickly. Its window is
		// its own, so this cannot renew the deadline of one already waiting.
		if (hasFreshOriginal) {
			attempt = 0;
		}
		startIfIdle();
	}

	return () => {
		listeners.delete(onChange);
		originals.forEach((original) => {
			const key = getOriginalKey(original);
			const nextCount = (subscriberCounts.get(key) || 1) - 1;
			if (nextCount <= 0) {
				subscriberCounts.delete(key);
			} else {
				subscriberCounts.set(key, nextCount);
			}
		});
		if (getUnresolvedOriginals().length === 0) {
			stop();
		}
	};
};

/** Test seam — the module state is deliberately global for the lifetime of the tab. */
export const resetConvertedImageStatusStore = () => {
	stop();
	refsByFileId.clear();
	refsByS3ObjectKey.clear();
	resolvedKeys.clear();
	askedKeys.clear();
	elapsedMsByKey.clear();
	gaveUpKeys.clear();
	subscriberCounts.clear();
	originalsByKey.clear();
	listeners.clear();
	attempt = 0;
	inFlight = false;
	version = 0;
};
